// Propósito: cliente de API para el módulo de chatbot.
import api from '../../../lib/api';
import { getApiBaseUrl } from '../../../lib/httpClient';
import {
  ChatbotInstance,
  ChatbotBotInstance,
  ChatbotBotPublic,
  ChatbotConversation,
  ChatbotMessage,
  ChatbotWhitelistEntry,
  ChatbotMetrics,
  ChatbotPublicConfig,
  CreateInstanceRequest,
  UpdateInstanceRequest,
  CreateBotRequest,
  UpdateBotRequest,
  ChatbotBot,
  ChatbotBotDetail,
  ChatbotAiCatalog,
  ChatbotInfoField,
  ChatbotConversationInfoFieldsResponse,
  PatchBotInfoFieldsRequest,
  TenantAiKeysResponse,
  UpdateTenantAiKeysRequest,
  SendMessageRequest,
  ApiResponse,
  OtpResponse,
  SendMessageResponse,
  FlowiseUpload,
} from '../types/chatbot.types';

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success) {
    throw new Error(response.data.error || fallbackError);
  }
  if (!response.data.data) {
    throw new Error(fallbackError);
  }
  return response.data.data;
}

type ApiErrorPayload = {
  error?: string;
  message?: string;
};

export function getChatbotApiErrorMessage(error: unknown, fallbackError: string): string {
  const withResponse = error as {
    response?: { data?: ApiErrorPayload; status?: number };
    data?: ApiErrorPayload;
    message?: string;
  };

  const responseMessage =
    withResponse.response?.data?.error ||
    withResponse.response?.data?.message ||
    withResponse.data?.error ||
    withResponse.data?.message;

  if (responseMessage) {
    return responseMessage;
  }

  return withResponse.message || fallbackError;
}

type StreamEventHandlers = {
  onToken: (token: string) => void;
  onFinalMessage: (message: ChatbotMessage) => void;
  onError: (message: string) => void;
};

export type ChatbotExportFormat = 'JSON' | 'CSV' | 'PDF';

function createStreamingError(
  message: string,
  status?: number,
  data?: unknown
): Error & { status?: number; data?: unknown } {
  const error = new Error(message) as Error & { status?: number; data?: unknown };
  error.status = status;
  error.data = data;
  return error;
}

const STREAM_MESSAGE_TIMEOUT_MS = 300_000;

function decodeSseTokenPayload(eventData: string): string {
  if (!eventData) {
    return '';
  }

  const firstChar = eventData[0];
  if (firstChar === '"' || firstChar === '{') {
    try {
      const parsed = JSON.parse(eventData) as unknown;
      if (typeof parsed === 'string') {
        return parsed;
      }
    } catch {
      // Compatibilidad con tokens SSE en texto plano.
    }
  }

  return eventData;
}

function parseSseEventBlock(eventBlock: string): { eventName: string; eventData: string } {
  const lines = eventBlock.split('\n');
  let eventName = 'message';
  let eventData = '';

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.replace('event:', '').trim();
      continue;
    }
    if (line.startsWith('data:')) {
      eventData += line.replace('data:', '').trim();
    }
  }

  return { eventName, eventData };
}

function processSseEventBlock(eventBlock: string, handlers: StreamEventHandlers): void {
  const { eventName, eventData } = parseSseEventBlock(eventBlock);

  if (eventName === 'token') {
    handlers.onToken(decodeSseTokenPayload(eventData));
    return;
  }

  if (eventName === 'error') {
    handlers.onError(eventData || 'Error en streaming');
    return;
  }

  if (eventName === 'message') {
    try {
      const parsed = JSON.parse(eventData);
      if (parsed?.botMessage) {
        handlers.onFinalMessage(parsed.botMessage as ChatbotMessage);
      }
    } catch {
      // Ignorar errores de parseo en mensaje final
    }
  }
}

function handleStreamFailure(error: unknown, handlers: StreamEventHandlers): never {
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: string }).name === 'AbortError'
  ) {
    handlers.onError('Tiempo de espera agotado');
  } else {
    const message = error instanceof Error ? error.message : 'Error en streaming';
    handlers.onError(message);
  }
  throw error;
}

// ============================================
// Admin API
// ============================================

export const chatbotAdminApi = {
  // Instances
  async listInstances(tenantId?: string): Promise<ChatbotInstance[]> {
    const params = tenantId ? { tenantId } : {};
    const response = await api.get<ApiResponse<ChatbotInstance[]>>(`/admin/chatbot/instances`, {
      params,
    });
    return response.data.data || [];
  },

  async getInstance(id: string): Promise<ChatbotInstance | null> {
    const response = await api.get<ApiResponse<ChatbotInstance>>(`/admin/chatbot/instances/${id}`);
    return response.data.data || null;
  },

  async createInstance(data: CreateInstanceRequest): Promise<ChatbotInstance> {
    try {
      const response = await api.post<ApiResponse<ChatbotInstance>>(
        `/admin/chatbot/instances`,
        data,
        { timeout: 30000 } // 30 segundos timeout
      );

      if (!response.data) {
        throw new Error('Respuesta vacía del servidor');
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al crear instancia');
      }

      if (!response.data.data) {
        throw new Error('El servidor no devolvió la instancia creada');
      }

      return response.data.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.error ||
            error.response.data?.message ||
            `Error ${error.response.status}`
        );
      } else if (error.request) {
        throw new Error('El servidor no respondió. Verifica tu conexión.');
      } else {
        throw error;
      }
    }
  },

  async updateInstance(id: string, data: UpdateInstanceRequest): Promise<ChatbotInstance> {
    const response = await api.put<ApiResponse<ChatbotInstance>>(
      `/admin/chatbot/instances/${id}`,
      data
    );
    return requireData(response, 'Error al actualizar instancia');
  },

  async deleteInstance(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/admin/chatbot/instances/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al eliminar instancia');
    }
  },

  // Bots
  async listBots(instanceId: string): Promise<ChatbotBotInstance[]> {
    const response = await api.get<ApiResponse<ChatbotBotInstance[]>>(
      `/admin/chatbot/instances/${instanceId}/bots`
    );
    return response.data.data || [];
  },

  async createBot(instanceId: string, data: CreateBotRequest): Promise<ChatbotBotInstance> {
    try {
      const response = await api.post<ApiResponse<ChatbotBotInstance>>(
        `/admin/chatbot/instances/${instanceId}/bots`,
        data
      );
      return requireData(response, 'Error al crear bot');
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al crear bot'));
    }
  },

  async updateBot(
    instanceId: string,
    botId: string,
    data: UpdateBotRequest
  ): Promise<ChatbotBotInstance> {
    try {
      const response = await api.put<ApiResponse<ChatbotBotInstance>>(
        `/admin/chatbot/instances/${instanceId}/bots/${botId}`,
        data
      );
      return requireData(response, 'Error al actualizar bot');
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al actualizar bot'));
    }
  },

  async deleteBot(instanceId: string, botId: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/admin/chatbot/instances/${instanceId}/bots/${botId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al eliminar bot');
    }
  },

  async deleteIndependentBot(botId: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(
        `/admin/chatbot/bots/${botId.slice(0, 64)}`
      );
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al eliminar bot');
      }
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al eliminar bot'));
    }
  },

  async listAllBots(): Promise<ChatbotBotInstance[]> {
    const response = await api.get<ApiResponse<ChatbotBotInstance[]>>('/admin/chatbot/bots');
    return response.data.data || [];
  },

  async getBotAiCatalog(tenantId?: string): Promise<ChatbotAiCatalog> {
    const path = tenantId
      ? `/admin/chatbot/bots/catalog?tenantId=${encodeURIComponent(tenantId)}`
      : '/admin/chatbot/bots/catalog';
    const response = await api.get<ApiResponse<ChatbotAiCatalog>>(path);
    return requireData(response, 'Error al cargar catálogo IA');
  },

  async getBotDetail(botId: string): Promise<ChatbotBotDetail> {
    const response = await api.get<ApiResponse<ChatbotBotDetail>>(
      `/admin/chatbot/bots/${botId}`
    );
    return requireData(response, 'Error al obtener bot');
  },

  async createIndependentBot(data: CreateBotRequest): Promise<ChatbotBot> {
    const response = await api.post<ApiResponse<ChatbotBot>>('/admin/chatbot/bots', data);
    return requireData(response, 'Error al crear bot');
  },

  async updateIndependentBot(botId: string, data: UpdateBotRequest): Promise<ChatbotBot> {
    const response = await api.put<ApiResponse<ChatbotBot>>(
      `/admin/chatbot/bots/${botId}`,
      data
    );
    return requireData(response, 'Error al actualizar bot');
  },

  async patchBotInfoFields(
    botId: string,
    data: PatchBotInfoFieldsRequest
  ): Promise<{
    id: string;
    botInfoFieldsEnabled: boolean;
    userInfoFieldsEnabled: boolean;
    infoFields: ChatbotInfoField[];
  }> {
    try {
      const response = await api.patch<
        ApiResponse<{
          id: string;
          botInfoFieldsEnabled: boolean;
          userInfoFieldsEnabled: boolean;
          infoFields: ChatbotInfoField[];
        }>
      >(`/admin/chatbot/bots/${botId}/info-fields`, data);
      return requireData(response, 'Error al actualizar info del bot');
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al actualizar info del bot'));
    }
  },

  async getTenantAiKeys(tenantId?: string): Promise<TenantAiKeysResponse> {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/ai-keys`
      : '/admin/chatbot/tenant/ai-keys';
    const response = await api.get<ApiResponse<TenantAiKeysResponse>>(path);
    return requireData(response, 'Error al obtener API keys del tenant');
  },

  async updateTenantAiKeys(
    data: UpdateTenantAiKeysRequest,
    tenantId?: string,
  ): Promise<TenantAiKeysResponse> {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/ai-keys`
      : '/admin/chatbot/tenant/ai-keys';
    const response = await api.put<ApiResponse<TenantAiKeysResponse>>(path, data);
    return requireData(response, 'Error al actualizar API keys del tenant');
  },

  async probeLocalLlm(tenantId: string | undefined, baseUrl: string): Promise<string[]> {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/local-llm/probe`
      : '/admin/chatbot/tenant/local-llm/probe';
    const response = await api.post<ApiResponse<{ modelIds: string[] }>>(path, { baseUrl });
    const data = requireData(response, 'Error al listar modelos locales');
    return data.modelIds;
  },

  async getTenantEvoHubCredentials(tenantId?: string) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/evohub-credentials`
      : '/admin/chatbot/tenant/evohub-credentials';
    const response = await api.get<ApiResponse<import('@workspace/types').TenantEvoHubCredentialsStatus>>(path);
    return requireData(response, 'Error al obtener credenciales Evo Hub');
  },

  async updateTenantEvoHubCredentials(
    data: import('@workspace/types').UpdateTenantEvoHubCredentialsRequest,
    tenantId?: string,
  ) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/evohub-credentials`
      : '/admin/chatbot/tenant/evohub-credentials';
    const response = await api.put<ApiResponse<import('@workspace/types').TenantEvoHubCredentialsStatus>>(
      path,
      data,
    );
    return requireData(response, 'Error al actualizar credenciales Evo Hub');
  },

  async listTenantEmailAccounts(tenantId?: string) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/email`
      : '/admin/chatbot/tenant/email';
    const response = await api.get<
      ApiResponse<import('@workspace/types').TenantEmailAccountsListResponse>
    >(path);
    const data = requireData(response, 'Error al listar cuentas de correo');
    return data.accounts;
  },

  async createTenantEmailAccount(
    body: import('@workspace/types').UpsertTenantEmailAccountRequest,
    tenantId?: string,
  ) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/email`
      : '/admin/chatbot/tenant/email';
    const response = await api.post<ApiResponse<import('@workspace/types').TenantEmailAccountPublic>>(
      path,
      body,
    );
    return requireData(response, 'Error al crear cuenta de correo');
  },

  async updateTenantEmailAccount(
    accountId: string,
    body: import('@workspace/types').UpsertTenantEmailAccountRequest,
    tenantId?: string,
  ) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/email/${accountId}`
      : `/admin/chatbot/tenant/email/${accountId}`;
    const response = await api.patch<ApiResponse<import('@workspace/types').TenantEmailAccountPublic>>(
      path,
      body,
    );
    return requireData(response, 'Error al actualizar cuenta de correo');
  },

  async testTenantEmailAccount(
    accountId: string,
    body: import('@workspace/types').TestTenantEmailAccountRequest,
    tenantId?: string,
  ) {
    const path = tenantId
      ? `/admin/chatbot/tenant/${tenantId}/email/${accountId}/test`
      : `/admin/chatbot/tenant/email/${accountId}/test`;
    const response = await api.post<ApiResponse<import('@workspace/types').TestTenantEmailAccountResponse>>(
      path,
      body,
    );
    return requireData(response, 'Error al probar cuenta de correo');
  },

  // Whitelist
  async listWhitelist(instanceId: string): Promise<ChatbotWhitelistEntry[]> {
    const response = await api.get<ApiResponse<ChatbotWhitelistEntry[]>>(
      `/admin/chatbot/instances/${instanceId}/whitelist`
    );
    return response.data.data || [];
  },

  async addToWhitelist(instanceId: string, email: string): Promise<ChatbotWhitelistEntry> {
    const response = await api.post<ApiResponse<ChatbotWhitelistEntry>>(
      `/admin/chatbot/instances/${instanceId}/whitelist`,
      { email }
    );
    return requireData(response, 'Error al agregar email');
  },

  async removeFromWhitelist(instanceId: string, email: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/admin/chatbot/instances/${instanceId}/whitelist/${encodeURIComponent(email)}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al eliminar email');
    }
  },

  async whitelistImport(
    instanceId: string,
    file: File
  ): Promise<{ imported: number; failed: number }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<{ imported: number; failed: number }>>(
      `/admin/chatbot/instances/${instanceId}/whitelist/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return requireData(response, 'Error al importar whitelist');
  },

  async whitelistExport(instanceId: string): Promise<Blob> {
    const response = await api.get(`/admin/chatbot/instances/${instanceId}/whitelist/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Metrics
  async getMetrics(instanceId: string): Promise<ChatbotMetrics> {
    const response = await api.get<ApiResponse<ChatbotMetrics>>(
      `/admin/chatbot/instances/${instanceId}/metrics`
    );
    return (
      response.data.data || {
        totalConversations: 0,
        totalMessages: 0,
        avgResponseTime: 0,
        limitViolations: 0,
        bots: [],
      }
    );
  },

  async getTenantMetrics(tenantId: string): Promise<unknown> {
    const response = await api.get<ApiResponse<unknown>>(
      `/admin/chatbot/tenant/${tenantId}/metrics`
    );
    return requireData(response, 'Error al obtener métricas del tenant');
  },

  async getUsageSummary(tenantId?: string): Promise<unknown> {
    const response = await api.get<ApiResponse<unknown>>(`/admin/chatbot/usage-summary`, {
      params: tenantId ? { tenantId } : {},
    });
    return requireData(response, 'Error al obtener resumen de uso');
  },

  async getUserMetrics(instanceId: string, userId: string): Promise<unknown> {
    const response = await api.get<ApiResponse<unknown>>(
      `/admin/chatbot/instances/${instanceId}/users/${userId}/metrics`
    );
    return requireData(response, 'Error al obtener métricas del usuario');
  },

  async getTopUsers(
    instanceId: string,
    limit = 10
  ): Promise<
    Array<{
      userId: string;
      identifier: string;
      messageCount: number;
      conversationCount: number;
    }>
  > {
    const response = await api.get<
      ApiResponse<
        Array<{
          userId: string;
          identifier: string;
          messageCount: number;
          conversationCount: number;
        }>
      >
    >(`/admin/chatbot/instances/${instanceId}/users/top`, { params: { limit } });
    return response.data.data || [];
  },

  async exportConversations(
    instanceId: string,
    format: ChatbotExportFormat,
    botId?: string
  ): Promise<Blob> {
    const response = await api.get(`/admin/chatbot/instances/${instanceId}/export`, {
      params: botId ? { format, botId } : { format },
      responseType: 'blob',
    });
    return response.data;
  },

  async exportUserConversations(
    instanceId: string,
    userId: string,
    format: ChatbotExportFormat
  ): Promise<Blob> {
    const response = await api.get(
      `/admin/chatbot/instances/${instanceId}/users/${userId}/export`,
      { params: { format }, responseType: 'blob' }
    );
    return response.data;
  },

  async exportSingleConversation(
    instanceId: string,
    conversationId: string,
    format: ChatbotExportFormat
  ): Promise<Blob> {
    const response = await api.get(
      `/admin/chatbot/instances/${instanceId}/conversations/${conversationId}/export`,
      { params: { format }, responseType: 'blob' }
    );
    return response.data;
  },

  async exportMetrics(tenantId?: string): Promise<unknown> {
    const response = await api.get<ApiResponse<unknown>>(`/admin/chatbot/export/metrics`, {
      params: tenantId ? { tenantId } : {},
    });
    return requireData(response, 'Error al exportar métricas');
  },

  // Tenants (SUPERADMIN only)
  async listTenants(): Promise<Array<{ id: string; name: string; userCount: number }>> {
    const response =
      await api.get<ApiResponse<Array<{ id: string; name: string; userCount: number }>>>(
        `/admin/chatbot/tenants`
      );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al listar tenants');
    }
    return response.data.data || [];
  },

  // Admin Conversations
  async listAllConversations(
    instanceId: string,
    limit = 100,
    offset = 0
  ): Promise<ChatbotConversation[]> {
    const response = await api.get<ApiResponse<ChatbotConversation[]>>(
      `/admin/chatbot/instances/${instanceId}/conversations`,
      { params: { limit, offset } }
    );
    return response.data.data || [];
  },

  async getConversationMessages(
    instanceId: string,
    conversationId: string,
    limit = 5000
  ): Promise<ChatbotMessage[]> {
    const response = await api.get<ApiResponse<ChatbotMessage[]>>(
      `/admin/chatbot/instances/${instanceId}/conversations/${conversationId}/messages`,
      { params: { limit } }
    );
    return response.data.data || [];
  },

  async deleteConversation(
    instanceId: string,
    conversationId: string
  ): Promise<{ conversationId: string; deletedFiles: number }> {
    const response = await api.delete<ApiResponse<{ conversationId: string; deletedFiles: number }>>(
      `/admin/chatbot/instances/${instanceId}/conversations/${conversationId}`
    );
    return requireData(response, 'Error al eliminar conversación');
  },

  async getConversationInfoFields(
    instanceId: string,
    conversationId: string
  ): Promise<ChatbotConversationInfoFieldsResponse> {
    try {
      const response = await api.get<ApiResponse<ChatbotConversationInfoFieldsResponse>>(
        `/admin/chatbot/instances/${instanceId}/conversations/${conversationId}/info-fields`
      );
      return requireData(response, 'Error al obtener info del contacto');
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al obtener info del contacto'));
    }
  },

  async updateConversationInfoFields(
    instanceId: string,
    conversationId: string,
    infoFields: ChatbotInfoField[]
  ): Promise<ChatbotConversationInfoFieldsResponse> {
    try {
      const response = await api.put<ApiResponse<ChatbotConversationInfoFieldsResponse>>(
        `/admin/chatbot/instances/${instanceId}/conversations/${conversationId}/info-fields`,
        { infoFields }
      );
      return requireData(response, 'Error al guardar info del contacto');
    } catch (error: unknown) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al guardar info del contacto'));
    }
  },

  // Health
  async getInstanceHealth(instanceId: string): Promise<{
    instanceId: string;
    instanceName: string;
    hasAuthErrors: boolean;
    bots: Array<{
      botId: string;
      botName: string;
      status: 'online' | 'error' | 'offline';
      lastChecked: string;
    }>;
  }> {
    const response = await api.get<ApiResponse<any>>(
      `/admin/chatbot/instances/${instanceId}/health`
    );
    return response.data.data;
  },
};

// ============================================
// Public Chat API
// ============================================

export const chatbotPublicApi = {
  // Public config (no auth required)
  async getPublicConfig(instanceId: string): Promise<ChatbotPublicConfig> {
    const response = await api.get<ApiResponse<ChatbotPublicConfig>>(
      `/chatbot/${instanceId}/config`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Error al obtener configuración');
    }
    return response.data.data;
  },

  // Auth
  async anonymousAccess(instanceId: string, anonymousId?: string): Promise<void> {
    const response = await api.post<ApiResponse<void>>(
      `/chatbot/${instanceId}/auth/anonymous`,
      anonymousId ? { anonymousId } : {}
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error de acceso');
    }
  },

  async redeemLink(
    instanceId: string,
    token: string,
  ): Promise<{ botId: string; conversationId: string }> {
    const response = await api.post<ApiResponse<{ botId: string; conversationId: string }>>(
      `/chatbot/${instanceId}/auth/link`,
      { token: token.slice(0, 512) },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Enlace inválido o vencido');
    }
    return response.data.data;
  },

  async requestOtp(
    instanceId: string,
    payload: { email?: string; phone?: string }
  ): Promise<OtpResponse> {
    try {
      const response = await api.post<OtpResponse>(
        `/chatbot/${instanceId}/auth/request-otp`,
        payload
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.error ||
            error.response.data?.message ||
            `Error ${error.response.status}`
        );
      } else if (error.request) {
        throw new Error('El servidor no respondió. Verifica tu conexión.');
      } else {
        throw error;
      }
    }
  },

  async verifyOtp(
    instanceId: string,
    payload: { email?: string; phone?: string; code: string }
  ): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(
        `/chatbot/${instanceId}/auth/verify-otp`,
        payload
      );
      if (!response.data.success) {
        throw new Error(response.data.error || 'Código inválido');
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.error ||
            error.response.data?.message ||
            `Error ${error.response.status}`
        );
      } else if (error.request) {
        throw new Error('El servidor no respondió. Verifica tu conexión.');
      } else {
        throw error;
      }
    }
  },

  async createPlatformSession(instanceId: string): Promise<void> {
    const response = await api.post<ApiResponse<void>>(
      `/platform/auth/chatbots/${instanceId}/session`,
      {}
    );
    if (!response.data.success) {
      throw new Error(
        response.data.error || response.data.message || 'No se pudo iniciar sesión en el chatbot'
      );
    }
  },

  async logout(instanceId: string): Promise<void> {
    await api.post(`/chatbot/${instanceId}/auth/logout`, {});
  },

  // Bots & Conversations
  async listBots(instanceId: string): Promise<ChatbotBotPublic[]> {
    try {
      const response = await api.get<ApiResponse<ChatbotBotPublic[]>>(
        `/chatbot/${instanceId}/bots`,
      );
      return response.data.data || [];
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { error?: string; message?: string } };
        };
        const message =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          (axiosError.response?.status === 401
            ? 'Sesión no válida. Por favor inicie sesión nuevamente.'
            : undefined);
        if (message) {
          throw new Error(message);
        }
      }
      throw error;
    }
  },

  async listConversations(instanceId: string, botId: string): Promise<ChatbotConversation[]> {
    const response = await api.get<ApiResponse<ChatbotConversation[]>>(
      `/chatbot/${instanceId}/bots/${botId}/conversations`
    );
    return response.data.data || [];
  },

  async createConversation(instanceId: string, botId: string): Promise<ChatbotConversation> {
    const response = await api.post<ApiResponse<ChatbotConversation>>(
      `/chatbot/${instanceId}/bots/${botId}/conversations`,
      {}
    );
    return requireData(response, 'Error al crear conversación');
  },

  async getOrCreateConversation(instanceId: string, botId: string): Promise<ChatbotConversation> {
    const response = await api.get<ApiResponse<ChatbotConversation>>(
      `/chatbot/${instanceId}/bots/${botId}/conversation`
    );
    return requireData(response, 'Error al obtener conversación');
  },

  async hideConversation(instanceId: string, conversationId: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/chatbot/${instanceId}/conversations/${conversationId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al ocultar conversación');
    }
  },

  // Messages
  async listMessages(
    instanceId: string,
    conversationId: string,
    limit?: number,
    offset?: number,
    order?: 'asc' | 'desc'
  ): Promise<ChatbotMessage[]> {
    const params: Record<string, string> = {};
    if (limit) params.limit = String(limit);
    if (offset) params.offset = String(offset);
    if (order) params.order = order;

    const response = await api.get<ApiResponse<ChatbotMessage[]>>(
      `/chatbot/${instanceId}/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data.data || [];
  },

  async sendMessage(
    instanceId: string,
    conversationId: string,
    data: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await api.post<ApiResponse<SendMessageResponse>>(
      `/chatbot/${instanceId}/conversations/${conversationId}/messages`,
      data
    );
    return requireData(response, 'Error al enviar mensaje');
  },

  async streamMessage(
    instanceId: string,
    conversationId: string,
    data: SendMessageRequest,
    handlers: StreamEventHandlers
  ): Promise<void> {
    const baseUrl = api.defaults.baseURL || getApiBaseUrl();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), STREAM_MESSAGE_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${baseUrl}/chatbot/${instanceId}/conversations/${conversationId}/messages?streaming=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...data, streaming: true }),
          credentials: 'include',
          signal: controller.signal,
        }
      );

      if (!response.ok || !response.body) {
        const errorPayload = await response.json().catch(() => null);
        const message = errorPayload?.error || `Error ${response.status} al iniciar streaming`;
        throw createStreamingError(message, response.status, errorPayload);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (part.trim()) {
            processSseEventBlock(part, handlers);
          }
        }
      }

      if (buffer.trim()) {
        processSseEventBlock(buffer, handlers);
      }
    } catch (error) {
      handleStreamFailure(error, handlers);
    } finally {
      window.clearTimeout(timeoutId);
    }
  },

  async uploadFile(
    instanceId: string,
    payload: {
      file: File;
      botId: string;
      conversationId: string;
      contentType: 'audio' | 'image' | 'file';
    }
  ): Promise<{
    upload: FlowiseUpload;
    fileUrl: string;
    fileMetadata: { name: string; size: number; mimeType: string };
  }> {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('botId', payload.botId);
    formData.append('conversationId', payload.conversationId);
    formData.append('contentType', payload.contentType);

    try {
      const response = await api.post<
        ApiResponse<{
          upload: FlowiseUpload;
          fileUrl: string;
          fileMetadata: { name: string; size: number; mimeType: string };
        }>
      >(`/chatbot/${instanceId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return requireData(response, 'Error al subir archivo');
    } catch (error) {
      throw new Error(getChatbotApiErrorMessage(error, 'Error al subir archivo'));
    }
  },

  async checkLimits(instanceId: string): Promise<{
    allowed: boolean;
    reason?: 'hourly' | 'daily' | 'total';
    resetAt?: string;
    remaining?: number;
  }> {
    const response = await api.get<
      ApiResponse<{
        allowed: boolean;
        reason?: 'hourly' | 'daily' | 'total';
        resetAt?: string;
        remaining?: number;
      }>
    >(`/chatbot/${instanceId}/limits`);
    return requireData(response, 'Error al verificar límites');
  },
};
